import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, Umbrella, Truck, Award, Home } from 'lucide-react';
import { useT } from '../utils/i18n';
// Compact trust strip — quiet, earned, not loud. Sits directly above the
// footer so the last thing a skeptical visitor sees is proof we're a real,
// licensed local operator (something national marketplaces structurally
// cannot match).
//
// Auto-cycles through one credential at a time on a 3-second interval so
// each item gets a moment of focus instead of competing for attention in a
// dense grid. Pauses on hover for accessibility and includes dot
// indicators so users can see the rotation progress and jump manually.
const ROTATE_MS = 3000;
export function CredentialsBar() {
  const t = useT();
  const credentials = [{
    icon: ShieldCheck,
    eyebrow: t('credentials.licensed'),
    value: 'WA UBI #604-XXX-XXX'
  }, {
    icon: Umbrella,
    eyebrow: t('credentials.insured'),
    value: '$2M General Liability'
  }, {
    icon: Truck,
    eyebrow: t('credentials.wsdot'),
    value: t('credentials.permitted')
  }, {
    icon: Award,
    eyebrow: t('credentials.bbb'),
    value: t('credentials.rating')
  }, {
    icon: Home,
    eyebrow: t('credentials.family'),
    value: 'Bonney Lake, WA'
  }];
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => {
      setIdx(p => (p + 1) % credentials.length);
    }, ROTATE_MS);
    return () => clearInterval(id);
  }, [paused, credentials.length]);
  const current = credentials[idx];
  const Icon = current.icon;
  return <section aria-label="Credentials and certifications" className="w-full bg-stone-100 dark:bg-[#1f1f1f] border-y border-stone-200 dark:border-white/5" data-id="element-515">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8" data-id="element-516">
        {/* Eyebrow */}
        <div className="flex items-center justify-center mb-5 md:mb-6" data-id="element-517">
          <div className="h-px w-8 bg-stone-300 dark:bg-white/15" data-id="element-518" />
          <span className="px-3 text-[10px] font-bold uppercase tracking-[0.18em] text-stone-500 dark:text-white/50" data-id="element-519">
            {t('credentials.eyebrow')}
          </span>
          <div className="h-px w-8 bg-stone-300 dark:bg-white/15" data-id="element-520" />
        </div>

        {/* Rotating credential card */}
        <div className="relative flex items-center justify-center min-h-[64px]" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)} data-id="element-521">
          <AnimatePresence mode="wait" data-id="element-522">
            <motion.div key={current.eyebrow} initial={{
            opacity: 0,
            y: 10,
            filter: 'blur(4px)'
          }} animate={{
            opacity: 1,
            y: 0,
            filter: 'blur(0px)'
          }} exit={{
            opacity: 0,
            y: -10,
            filter: 'blur(4px)'
          }} transition={{
            duration: 0.45,
            ease: [0.22, 1, 0.36, 1]
          }} className="flex items-center gap-4 px-4" aria-live="polite" data-id="element-523">
              <Icon className="w-6 h-6 md:w-7 md:h-7 text-stone-500 dark:text-white/60 shrink-0" strokeWidth={1.5} data-id="element-524" />
              <div className="min-w-0 text-left" data-id="element-525">
                <p className="text-[10px] md:text-[11px] font-bold uppercase tracking-[0.14em] text-stone-500 dark:text-white/50 leading-tight" data-id="element-526">
                  {current.eyebrow}
                </p>
                <p className="text-base md:text-lg font-semibold text-stone-900 dark:text-white leading-snug" data-id="element-527">
                  {current.value}
                </p>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Dot indicators */}
        <div className="flex items-center justify-center gap-1.5 mt-4" role="tablist" aria-label="Select credential" data-id="element-528">
          {credentials.map((cred, i) => <button key={cred.eyebrow} type="button" role="tab" aria-selected={i === idx} aria-label={`Show ${cred.eyebrow}`} onClick={() => setIdx(i)} className="group p-1 -m-1" data-id="element-529">
              <span className={`block h-1.5 rounded-full transition-all duration-300 ${i === idx ? 'w-6 bg-stone-700 dark:bg-white/80' : 'w-1.5 bg-stone-300 dark:bg-white/20 group-hover:bg-stone-400 dark:group-hover:bg-white/40'}`} data-id="element-530" />
            </button>)}
        </div>
      </div>
    </section>;
}